import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Loader, AlertCircle } from 'lucide-react';
import axios from 'axios';
import { signInWithEmailAndPassword, AuthError } from 'firebase/auth';
import { auth } from '../services/firebase';
import iconQFlowLarge from '../assets/icon-qflow-large.png';

const HUB_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'https://qflow.xflow.ca:5005';

// Mapeia códigos de erro do Firebase para mensagens em português
function firebaseErrorMessage(code: string): string {
  switch (code) {
    case 'auth/user-not-found':
    case 'auth/wrong-password':
    case 'auth/invalid-credential':
      return 'E-mail ou senha inválidos. Tente novamente.';
    case 'auth/invalid-email':
      return 'Formato de e-mail inválido.';
    case 'auth/user-disabled':
      return 'Esta conta foi desativada. Entre em contato com o suporte.';
    case 'auth/too-many-requests':
      return 'Muitas tentativas. Aguarde alguns minutos e tente novamente.';
    case 'auth/network-request-failed':
      return 'Falha de conexão. Verifique sua internet e tente novamente.';
    default:
      return 'Erro ao autenticar. Tente novamente.';
  }
}

export default function Login() {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ usuario: '', senha: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      // Passo 1: Autenticação via Firebase (email + senha)
      const userCredential = await signInWithEmailAndPassword(
        auth,
        credentials.usuario,
        credentials.senha
      );

      // Firebase ID Token — válido por 1h, renovável automaticamente
      const idToken = await userCredential.user.getIdToken();

      // Limpa sessão anterior
      localStorage.removeItem('tenantId');
      localStorage.removeItem('userCode');
      localStorage.removeItem('user_info');

      // Armazena token — o interceptor do apiClient já o injeta em cada request
      localStorage.setItem('jwt_token', idToken);

      let tenantId: string | null = null;
      let hubUsuario: string | null = null;
      const email = userCredential.user.email || credentials.usuario;

      // Passo 2: Busca tenantId no Hub (MongoDB)
      try {
        const hubResp = await axios.get(`${HUB_BASE_URL}/site/api/hub/Usuario`, {
          params: { q: email },
          headers: { Authorization: `Bearer ${idToken}` },
          timeout: 10000,
        });
        const hubData = Array.isArray(hubResp.data) ? hubResp.data[0] : hubResp.data;
        tenantId = hubData?.tenantId || hubData?.empresa || null;
        hubUsuario = hubData?.usuario || null;
      } catch (hubErr) {
        console.warn('Hub /Usuario falhou, usando e-mail como fallback:', hubErr);
      }

      // Fallback: usa o e-mail/login como identificador
      if (!tenantId) {
        console.warn('tenantId não encontrado — usando e-mail como fallback.');
        tenantId = email;
      }

      localStorage.setItem('tenantId', tenantId);
      localStorage.setItem('userCode', email);
      localStorage.setItem('user_info', JSON.stringify({
        usuario: email,
        // usuário do cadastro (ex.: BABYJOY) — identifica o influencer em "Minhas Indicações"
        login: hubUsuario || email,
        nome: userCredential.user.displayName || email,
        tenantId,
        uid: userCredential.user.uid,
      }));

      navigate('/');

    } catch (err: any) {
      console.error('Erro na autenticação:', err);
      const code: string = (err as AuthError)?.code || '';
      setError(firebaseErrorMessage(code));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">

      {/* ── Painel esquerdo: Brand ── */}
      <div
        className="hidden lg:flex lg:w-1/2 xl:w-[55%] flex-col justify-between p-12 relative overflow-hidden"
        style={{ background: 'linear-gradient(145deg, #5B3FE0 0%, #4E2FCE 40%, #3D22B0 100%)' }}
      >
        {/* Decorações de fundo */}
        <div className="absolute inset-0 pointer-events-none overflow-hidden">
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full"
               style={{ background: 'rgba(123,92,250,0.25)', filter: 'blur(80px)' }} />
          <div className="absolute -bottom-40 -left-20 w-[400px] h-[400px] rounded-full"
               style={{ background: 'rgba(91,63,224,0.3)', filter: 'blur(100px)' }} />
          {/* Grid sutil */}
          <div className="absolute inset-0 opacity-[0.06]"
               style={{
                 backgroundImage: 'linear-gradient(rgba(255,255,255,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.8) 1px, transparent 1px)',
                 backgroundSize: '48px 48px',
               }} />
          {/* Círculos decorativos */}
          <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/2 w-80 h-80 rounded-full border border-white/10" />
          <div className="absolute top-1/2 right-0 -translate-y-1/2 translate-x-1/4 w-56 h-56 rounded-full border border-white/10" />
        </div>

        {/* Logo topo */}
        <div className="relative flex items-center gap-3">
          <img src={iconQFlowLarge} alt="QFlow" style={{ width: 44, height: 44 }} />
          <span className="text-white text-xl font-bold tracking-tight">QFlow</span>
        </div>

        {/* Conteúdo central */}
        <div className="relative space-y-8">
          <div>
            <h2 className="text-4xl xl:text-5xl font-bold text-white leading-tight">
              Hub de<br />Marketplaces<br />
              <span style={{ color: '#C9BBFF' }}>Inteligente</span>
            </h2>
            <p className="mt-4 text-white/70 text-lg leading-relaxed max-w-sm">
              Centralize, sincronize e escale sua presença em todos os canais de venda.
            </p>
          </div>

          <div className="space-y-4">
            {[
              { icon: '⚡', label: 'Integração em tempo real com +10 marketplaces' },
              { icon: '📦', label: 'Catálogo e pedidos unificados em um só lugar' },
              { icon: '📊', label: 'Dashboards e relatórios para decisões mais rápidas' },
            ].map((f) => (
              <div key={f.label} className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-lg flex items-center justify-center text-base"
                     style={{ background: 'rgba(255,255,255,0.15)' }}>
                  {f.icon}
                </div>
                <p className="text-white/80 text-sm leading-snug pt-1">{f.label}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Rodapé esquerdo */}
        <div className="relative">
          <p className="text-white/40 text-xs">© 2025 QFlow · Todos os direitos reservados</p>
        </div>
      </div>

      {/* ── Painel direito: Formulário ── */}
      <div className="flex-1 flex flex-col items-center justify-center p-8 bg-white">

        {/* Logo mobile (só aparece no mobile) */}
        <div className="lg:hidden flex items-center gap-2 mb-10">
          <img src={iconQFlowLarge} alt="QFlow" style={{ width: 36, height: 36 }} />
          <span className="text-dark-100 text-lg font-bold">QFlow</span>
        </div>

        <div className="w-full max-w-sm">

          {/* Cabeçalho do form */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-dark-100">Bem-vindo de volta</h1>
            <p className="text-dark-400 text-sm mt-1">Acesse sua conta para continuar</p>
          </div>

          {/* Erro */}
          {error && (
            <div className="flex items-center gap-3 p-3.5 bg-red-50 border border-red-200 rounded-xl mb-6">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <p className="text-red-600 text-sm">{error}</p>
            </div>
          )}

          <form onSubmit={handleLogin} className="space-y-5">
            {/* Usuário */}
            <div>
              <label className="block text-sm font-medium text-dark-200 mb-1.5">Usuário</label>
              <input
                type="text"
                value={credentials.usuario}
                onChange={(e) => setCredentials({ ...credentials, usuario: e.target.value })}
                placeholder="Seu usuário"
                required
                autoComplete="username"
                className="w-full px-4 py-3 bg-white border border-gray-200 hover:border-primary-400 focus:border-primary-500 rounded-xl text-dark-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm"
              />
            </div>

            {/* Senha */}
            <div>
              <label className="block text-sm font-medium text-dark-200 mb-1.5">Senha</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={credentials.senha}
                  onChange={(e) => setCredentials({ ...credentials, senha: e.target.value })}
                  placeholder="••••••••"
                  required
                  autoComplete="current-password"
                  className="w-full px-4 py-3 pr-11 bg-white border border-gray-200 hover:border-primary-400 focus:border-primary-500 rounded-xl text-dark-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 p-1.5 text-gray-400 hover:text-dark-300 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {/* Botão */}
            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 px-6 py-3.5 font-semibold rounded-xl text-white transition-all mt-2"
              style={
                loading
                  ? { background: '#8A7FCC', cursor: 'not-allowed' }
                  : { background: 'linear-gradient(135deg, #5B3FE0 0%, #4E2FCE 100%)', boxShadow: '0 4px 16px rgba(78,47,206,0.35)' }
              }
              onMouseEnter={(e) => {
                if (!loading) (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 20px rgba(78,47,206,0.5)';
              }}
              onMouseLeave={(e) => {
                if (!loading) (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(78,47,206,0.35)';
              }}
            >
              {loading ? (
                <>
                  <Loader className="w-4 h-4 animate-spin" />
                  Entrando...
                </>
              ) : (
                'Entrar'
              )}
            </button>
          </form>

          {/* Footer */}
          <p className="text-center text-gray-400 text-xs mt-8 flex items-center justify-center gap-1.5">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
              <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
            </svg>
            Conexão segura e criptografada
          </p>
        </div>
      </div>

    </div>
  );
}
